import React, {useCallback, useState, useMemo} from 'react'
import {Helmet} from 'react-helmet'
import {FormattedMessage} from 'react-intl'
import {useLocation, useParams} from 'react-router-dom'
import {useCategories, useProductSearch} from '@salesforce/commerce-sdk-react'
import {Box, Flex, useMultiStyleConfig} from '@chakra-ui/react'
import ManualsPageHeader from './partials/manuals-page-header'
import GridTemplate from '../../components/grid-template'
import GridCard from '../../components/grid-card'
import {CAT_MENU_DEFAULT_ROOT_CATEGORY} from '@salesforce/retail-react-app/app/constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'
import {useCustomGetCategory, fetchCustomCategory} from '../../custom-hooks/use-custom-categories'
import routingObject from '../../utils/static-routes'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import {useManualsPageMeta} from '../../hooks/use-category-meta'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import AlternateMetaTag from '../../components/metatag/alternate'
import {getSites} from '../../utils/site-utils'
import LoadingSpinner from '../../components/loading-spinner'
import {useGetQuickLinks} from '../../custom-api/hooks/use-get-quicklinks'
import QuickLinks from '../faq/partials/quick-links'
import {useCustomPreferenceWithLocale} from '../../hooks/use-custom-preference'
import loadable from '@loadable/component'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'

const PageNotFound = loadable(() => import('./../page-not-found'))

const Manuals = () => {
    const styles = useMultiStyleConfig('Manuals')

    const isServer = typeof window === 'undefined'

    const [productQuery, setProductQuery] = useState('')
    const location = useLocation()
    const {data: quickLinksData} = useGetQuickLinks()
    const {buildLocalizedUrl} = useMulticountryUtils()

    const {MANUALS_PATH} = routingObject

    const {site, locale} = useMultiSite()
    const {currency} = useCurrency()

    const params = useParams()
    const currentCategory = params.rest ? params.rest : CAT_MENU_DEFAULT_ROOT_CATEGORY

    const isNotRootCategory = currentCategory !== CAT_MENU_DEFAULT_ROOT_CATEGORY

    const categoryData = useGetCategoriesData()

    const {res} = useServerContext()

    const {
        data: categoryDetails,
        isLoading: isCategoryDetailsLoading,
        isError: isCategoryError,
    } = useCustomGetCategory(
        {
            parameters: {
                urlSegment: currentCategory,
            },
        },
        {
            enabled: isNotRootCategory,
        },
    )

    let displayNotFound = false
    if (isNotRootCategory && !isCategoryDetailsLoading && isCategoryError) {
        displayNotFound = true
    }

    const extractedId =
        categoryDetails?.id ||
        categoryDetails?.data?.id ||
        categoryDetails?.category?.id ||
        categoryDetails?.categorysfcc?.data?.[0]?.id

    const categoryId =
        isNotRootCategory && extractedId ? extractedId : CAT_MENU_DEFAULT_ROOT_CATEGORY

    const isCategoryReady = isNotRootCategory ? categoryId !== CAT_MENU_DEFAULT_ROOT_CATEGORY : true

    const {
        data: categoryResult,
        isLoading: isCategoriesListLoading,
        isError: isCategoriesListError,
    } = useCategories(
        {
            parameters: {
                levels: 1,
                ids: [categoryId],
                locale: locale.id,
            },
        },
        {enabled: !isNotRootCategory, keepPreviousData: true},
    )

    const categories = isNotRootCategory
        ? categoryDetails?.categories ||
          categoryDetails?.data?.categories ||
          categoryDetails?.categorysfcc?.data?.[0]?.categories
        : categoryResult?.data?.[0]?.categories

    const [manualShowResultOverride, setManualShowResultOverride] = useState(null)

    const isAutoShow = productQuery.length > 0

    const shouldShowResult =
        manualShowResultOverride !== null ? manualShowResultOverride : isAutoShow

    const handleSetShowResult = (value) => {
        setManualShowResultOverride(value)

        if (value === false) {
            setProductQuery('')
        }
    }

    const refine =
        shouldShowResult && !productQuery && categoryId !== CAT_MENU_DEFAULT_ROOT_CATEGORY
            ? ['cgid=' + categoryId]
            : []

    const {
        data: productSearchResult,
        isLoading: isProductSearchLoading,
        isError: isProductSearchError,
    } = useProductSearch(
        {
            parameters: {
                q: productQuery.length > 0 ? productQuery : '',
                refine,
                expand: ['represented_products', 'page_meta_tags'],
                perPricebook: true,
                allVariationProperties: true,
                locale: locale.id,
                site: site.id,
                currency,
            },
        },
        {enabled: !!(shouldShowResult && isCategoryReady), keepPreviousData: true},
    )

    const manualsCategoryHidden = useCustomPreferenceWithLocale('manualsCategoryHidden')

    const baseManualsUrl = buildLocalizedUrl(MANUALS_PATH)
    const {customParams} = getConfig()
    let canonicalUrl = `https://${customParams.currentHost}${baseManualsUrl}`
    if (isNotRootCategory && categoryDetails?.c_hreflangs) {
        const currentHreflangObj = categoryDetails.c_hreflangs.find(
            (item) => item.hreflang === locale.alias,
        )

        let url = currentHreflangObj ? currentHreflangObj.url : categoryDetails.c_hreflangs[0].url
        const split = url.split('/c/')
        if (split.length === 2) {
            url = `/${locale.alias}/manuals/c/${decodeURIComponent(split[1])}`
        }
        canonicalUrl = `https://${customParams.currentHost}${url}`
    }

    const getCategoryHref = useCallback(
        (category) => {
            const category_url = categoryData?.[category.id]?.url || ''
            return `${baseManualsUrl}${category_url}`
        },
        [categoryData, baseManualsUrl],
    )

    const handleInnerHeader = () => {
        if (productQuery.length > 0 && productSearchResult?.hits?.length > 0 && shouldShowResult) {
            return (
                <FormattedMessage
                    defaultMessage={`{number} results found for "{query}"`}
                    id="manuals-page.search-results"
                    values={{
                        number: productSearchResult?.hits?.length,
                        query: productQuery,
                    }}
                />
            )
        } else {
            return (
                <FormattedMessage
                    defaultMessage="Browse by category"
                    id="manuals-page.category-subheader"
                />
            )
        }
    }

    const getProductHref = (product) => {
        if (!product?.productId) return undefined
        return buildLocalizedUrl(`/s/${product.productId}`)
    }

    const isLoading = shouldShowResult
        ? !isServer && isProductSearchLoading && !isProductSearchError
        : isNotRootCategory
        ? isCategoryDetailsLoading
        : !isServer && isCategoriesListLoading && !isCategoriesListError

    const {metaTitle, metaDescription} = useManualsPageMeta(isNotRootCategory, categoryDetails)

    const currentPath = 'https://' + customParams.currentHost + location.pathname

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: metaTitle,
        description: metaDescription,
        url: currentPath,
    }

    const sites = useMemo(() => getSites(), [])
    const calculateHreflangLinks = (isNotRootCategory, categoryDetails, sites) => {
        if (isNotRootCategory && categoryDetails?.c_hreflangs) {
            return categoryDetails.c_hreflangs.map((item) => {
                let url = item.url
                const split = url.split('/c/')
                if (split.length === 2) {
                    url = `/${item.hreflang}/manuals/c/${decodeURIComponent(split[1])}`
                }
                return {hreflang: item.hreflang, url}
            })
        }
        return sites
            .map((site) =>
                site.l10n.supportedLocales.map((locale) => ({
                    hreflang: locale.alias,
                    url: `/${locale.alias}/manuals`,
                })),
            )
            .flat()
    }
    const hreflangLinks = calculateHreflangLinks(isNotRootCategory, categoryDetails, sites)

    if (displayNotFound) {
        if (res) res.status(404)
        return <PageNotFound />
    }

    return (
        <Flex {...styles.manualsPageContainer} data-testid="manuals-page-container">
            <AlternateMetaTag links={hreflangLinks} />
            <Helmet>
                {metaTitle && <title>{metaTitle}</title>}
                {metaDescription && <meta name="description" content={metaDescription} />}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>
            <ManualsPageHeader
                noResults={!!(productQuery && shouldShowResult && !productSearchResult?.hits)}
                categoryId={categoryId}
                setProductQuery={setProductQuery}
                setShowResult={handleSetShowResult}
                currentCategory={categoryDetails?.name}
            />
            <GridTemplate header={handleInnerHeader()} variant="manuals">
                {isLoading && <LoadingSpinner />}
                {!shouldShowResult ||
                (productQuery && shouldShowResult && !productSearchResult?.hits) ? (
                    <>
                        {categories ? (
                            categories.map((category) => {
                                // A category is hidden if its ID is in the custom preference and the value is 'false'.
                                const isHiddenByConfig =
                                    manualsCategoryHidden &&
                                    manualsCategoryHidden[category.id] === 'false'

                                // Categories with these terms in their ID should also be hidden.
                                const isGenerallyHidden =
                                    category?.id.includes('commerceAdditional') ||
                                    category?.id.includes('hidden')

                                // If the category is not hidden by any rule, render the card.
                                if (!isHiddenByConfig && !isGenerallyHidden) {
                                    return (
                                        <GridCard
                                            key={category.id}
                                            title={category.name}
                                            href={getCategoryHref(category)}
                                            image={category?.c_imageLink}
                                            variant="manuals"
                                        />
                                    )
                                }
                                // Otherwise, render nothing for this category.
                                return null
                            })
                        ) : (
                            // Empty fallback for SSR, so it won't be appended
                            <></>
                        )}
                    </>
                ) : (
                    productSearchResult?.hits &&
                    productSearchResult?.hits?.map((product, i) => (
                        <GridCard
                            key={i}
                            title={product.productName}
                            href={getProductHref(product)}
                            image={
                                product?.c_image?.url ||
                                product?.image?.link ||
                                product?.image?.disBaseLink
                            }
                            variant="manuals"
                        />
                    ))
                )}
            </GridTemplate>
            {quickLinksData && (
                <Box {...styles.quicklinkContainer}>
                    <QuickLinks
                        quickLinksData={quickLinksData?.quick_link}
                        title={quickLinksData?.links_title}
                    />
                </Box>
            )}
        </Flex>
    )
}
Manuals.getTemplateName = () => 'manuals-page'

/**
 * Server-Side Rendering (SSR) pre-fetching strategy for the Manuals page.
 * * WHY IT WAS INTRODUCED:
 * When a user directly lands on a subcategory URL (e.g., /manuals/c/coffee) via SSR,
 * the server needs the custom category data to resolve the actual category ID.
 * Without this pre-fetch, the `useCustomGetCategory` hook would trigger a network
 * request during the server render, leaving the component in an `isLoading` state.
 * This would cause the server to render and return a <LoadingSpinner /> permanently,
 * while subsequent Commerce SDK standard hooks would crash due to missing IDs or context.
 * * HOW IT WORKS:
 * This static method runs on the Node.js server *before* the React component mounts.
 * It manually triggers the custom API call (`fetchCustomCategory`) and injects the
 * response directly into React Query's cache (`queryClient`).
 * It is crucial that the query key used here strictly matches the one used by the
 * client-side hook. When the `Manuals` component finally renders, the hook instantly
 * reads the pre-fetched data from the cache, bypasses the loading state, and safely
 * renders the complete HTML grid.
 */
Manuals.getProps = async ({req, res, params}) => {
    const {api, queryClient} = res.locals
    const locale = res.locals.locale
    const site = res.locals.site

    const currentCategory = params.rest ? params.rest : CAT_MENU_DEFAULT_ROOT_CATEGORY
    const isNotRootCategory = currentCategory !== CAT_MENU_DEFAULT_ROOT_CATEGORY

    if (isNotRootCategory) {
        try {
            const token = api.clientConfig?.headers?.authorization || ''

            const protocol = req.headers['x-forwarded-proto'] || 'https'
            const origin = `${protocol}://${req.headers.host}`

            const parameters = {urlSegment: currentCategory}

            await queryClient.fetchQuery(
                ['getCategoryCustom', currentCategory, locale?.id || locale],
                () => fetchCustomCategory({parameters, token, locale, site, origin}),
            )
        } catch (error) {
            console.error('Errore nel prefetch della categoria custom in getProps:', error)
            if (res) res.status(404)
        }
    }

    return {}
}

export default Manuals
